import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { motion, MotionConfig } from "framer-motion";
import { Bot } from "lucide-react";
import { StatsStrip } from "./components/StatsStrip";
import { ChannelsCard } from "./components/ChannelsCard";
import { AiResponseSettingsCard } from "./components/AiResponseSettingsCard";
import { BusinessHoursCard } from "./components/BusinessHoursCard";
import { QuickRepliesCard } from "./components/QuickRepliesCard";
import { AutomationRulesCard } from "./components/AutomationRulesCard";
import { KnowledgeBaseCard } from "./components/KnowledgeBaseCard";
import { containerVariants } from "./components/motion";
import { useCopilotCommand, CopilotCommand } from "@/features/copilot/copilotBus";
import { useAutomationConfig } from "./hooks/useAutomationConfig";
import {
  initialStats,
  initialChannels,
  initialAiSettings,
  initialBusinessHours,
  initialQuickReplies,
  initialRules,
  initialKnowledgeBase,
  type ChannelId,
  type AiResponseSettings,
  type BusinessHoursSettings,
  type QuickReply,
  type AutomationRule,
} from "./mockAutomations";

/**
 * Automations — the AI auto-reply CONTROL surface.
 *
 * Distinct from:
 *  - Dashboard: operational overview / at-a-glance activity feed.
 *  - Analytics: historical charts and trend metrics.
 * This page holds no charts and only a thin performance strip; everything
 * below it is a live control that changes how the AI behaves — channel
 * toggles, tone/persona, business hours, canned replies, keyword rules, and
 * knowledge base freshness. All state is local/in-memory (no backend yet),
 * but every control is fully interactive so it can be demoed end to end.
 */
export function AutomationsPage() {
  const [stats] = useState(initialStats);
  const [channels, setChannels] = useState(initialChannels);
  const [aiSettings, setAiSettings] = useState<AiResponseSettings>(initialAiSettings);
  const [businessHours, setBusinessHours] = useState<BusinessHoursSettings>(initialBusinessHours);
  const [quickReplies, setQuickReplies] = useState<QuickReply[]>(initialQuickReplies);
  const [rules, setRules] = useState<AutomationRule[]>(initialRules);
  const [knowledgeBase, setKnowledgeBase] = useState(initialKnowledgeBase);

  // Load persisted settings (chatbot settingsJson) and hydrate local state once;
  // then persist changes back, debounced. Falls back to the mock defaults when
  // there's no config yet (empty DB).
  const { config, isLoading, save } = useAutomationConfig();
  const hydratedRef = useRef(false);

  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    setChannels(config.channels);
    setAiSettings(config.aiSettings);
    setBusinessHours(config.businessHours);
    setQuickReplies(config.quickReplies);
    setRules(config.rules);
    hydratedRef.current = true;
  }, [isLoading, config]);

  useEffect(() => {
    if (!hydratedRef.current) return;
    const t = setTimeout(
      () => save({ channels, aiSettings, businessHours, quickReplies, rules }),
      600,
    );
    return () => clearTimeout(t);
  }, [channels, aiSettings, businessHours, quickReplies, rules, save]);

  const toggleChannel = (id: ChannelId, enabled: boolean) => {
    setChannels((prev) => prev.map((c) => (c.id === id ? { ...c, enabled } : c)));
  };

  const patchAiSettings = (patch: Partial<AiResponseSettings>) => {
    setAiSettings((prev) => ({ ...prev, ...patch }));
  };

  const patchBusinessHours = (patch: Partial<BusinessHoursSettings>) => {
    setBusinessHours((prev) => ({ ...prev, ...patch }));
  };

  const addQuickReply = (reply: Omit<QuickReply, "id">) => {
    setQuickReplies((prev) => [...prev, { ...reply, id: `qr-${Date.now()}` }]);
  };
  const updateQuickReply = (id: string, patch: Partial<QuickReply>) => {
    setQuickReplies((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const removeQuickReply = (id: string) => {
    setQuickReplies((prev) => prev.filter((r) => r.id !== id));
  };

  const addRule = (rule: Omit<AutomationRule, "id">) => {
    setRules((prev) => [...prev, { ...rule, id: `rule-${Date.now()}` }]);
  };
  const removeRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  // Copilot: "add automation rule" → append a sensible default rule.
  useCopilotCommand(CopilotCommand.AutomationsAddRule, () => {
    addRule({ keyword: "refund", action: "escalate", actionDetail: "Escalate to human" });
  });

  const resyncKnowledgeBase = () => {
    setKnowledgeBase((prev) => ({ ...prev, lastSyncedAt: "Just now" }));
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4 md:space-y-5">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="space-y-5"
        >
          <StatsStrip stats={stats} />

          <ChannelsCard channels={channels} onToggle={toggleChannel} />

          <div className="grid gap-6 lg:grid-cols-2">
            <AiResponseSettingsCard settings={aiSettings} onChange={patchAiSettings} />
            <div data-copilot-target="automations-hours">
              <BusinessHoursCard settings={businessHours} onChange={patchBusinessHours} />
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-2">
            <QuickRepliesCard
              replies={quickReplies}
              onAdd={addQuickReply}
              onUpdate={updateQuickReply}
              onRemove={removeQuickReply}
            />
            <div data-copilot-target="automations-rules">
              <AutomationRulesCard
                rules={rules}
                quickReplies={quickReplies}
                onAdd={addRule}
                onRemove={removeRule}
              />
            </div>
          </div>

          <KnowledgeBaseCard status={knowledgeBase} onResync={resyncKnowledgeBase} />
        </motion.div>
        </div>
      </div>
    </MotionConfig>
  );
}
